import Keycloak, { type KeycloakConfig, type KeycloakTokenParsed } from 'keycloak-js';

import {
  assertArrayOfNonEmptyStrings,
  assertEmptyOrString,
  assertNonEmptyString,
  assertRecord,
} from '@/core/lib/AssertUtils';
import { getConfig, type SoaConfig } from '@/core/lib/Config';
import { captureError } from '@/plugins/apmAgent/lib/ApmUtils';
import { createKeycloak } from '@/plugins/authentication/lib/KeycloakFactory';

export type Config = KeycloakConfig & { debug?: boolean };

export enum Reseau {
  ALPTIS = 'ALPTIS',
  MILTIS = 'MILTIS',
}

export interface Distributeur {
  id: string;
  role: string;
  scopes: string[];
}

export interface LoggedInUser {
  id: string;
  isImpersonated: boolean;
  /**
   * Devra être déprécié dans le future, avec la disparition RSI
   */
  codeDistributeur?: string;
  reseau?: string;
  distributeur: Distributeur;
}

export type AuthenticationState = Pick<Keycloak, 'authenticated' | 'token' | 'tokenParsed' | 'idToken'> & {
  user: LoggedInUser | undefined;
  isTokenRefreshed: boolean;
  logoutUrl: string | undefined;
};

export type EventType = 'success' | 'error' | 'tokenExpired' | 'refreshSuccess' | 'refreshError' | 'logout';

export type Listener = (state: AuthenticationState, eventType: EventType, ...args: unknown[]) => void | Promise<void>;

export interface Authentication {
  readonly authenticate: () => Promise<AuthenticationState>;
  readonly updateToken: () => Promise<void>;
  readonly getState: () => AuthenticationState;
}

let authenticationPromise: Promise<Authentication> | undefined;

export class AuthenticationError extends Error {}

interface TokenParsed extends KeycloakTokenParsed {
  code_distributeur: string | undefined;
  reseau: string | undefined;
  compte: {
    id: string;
  };
  utilisateur: {
    id: string;
  };
  distributeur: {
    id: string;
    role: string;
  };
  scopes: string[];
}

function assertTokenParsed(tokenParsed: KeycloakTokenParsed | undefined): asserts tokenParsed is TokenParsed {
  assertRecord(tokenParsed, 'tokenParsed');

  assertEmptyOrString(tokenParsed.code_distributeur, 'tokenParsed.code_distributeur');
  assertEmptyOrString(tokenParsed.reseau, 'tokenParsed.reseau');

  assertRecord(tokenParsed.utilisateur, 'tokenParsed.utilisateur');
  assertNonEmptyString(tokenParsed.utilisateur.id, 'tokenParsed.utilisateur.id');

  assertRecord(tokenParsed.compte, 'tokenParsed.compte');
  assertNonEmptyString(tokenParsed.compte.id, 'tokenParsed.compte.id');

  assertRecord(tokenParsed.distributeur, 'tokenParsed.distributeur');
  assertNonEmptyString(tokenParsed.distributeur.id, 'tokenParsed.distributeur.id');
  assertNonEmptyString(tokenParsed.distributeur.role, 'tokenParsed.distributeur.role');

  assertArrayOfNonEmptyStrings(tokenParsed.scopes, 'tokenParsed.scopes');
}

function mapStateFromKeycloak(soaConfig: SoaConfig, keycloak: Keycloak): AuthenticationState {
  const tokenParsed = keycloak.tokenParsed;

  let user: LoggedInUser | undefined;
  let logoutUrl: string | undefined = undefined;

  if (keycloak.authenticated) {
    logoutUrl = getLogoutUrl(soaConfig, keycloak.idToken);

    try {
      assertTokenParsed(tokenParsed);

      user = {
        id: tokenParsed.utilisateur.id,
        isImpersonated: tokenParsed.compte.id !== tokenParsed.utilisateur.id,
        codeDistributeur: tokenParsed.code_distributeur,
        reseau: tokenParsed.reseau,
        distributeur: {
          id: tokenParsed.distributeur.id,
          scopes: tokenParsed.scopes,
          role: tokenParsed.distributeur.role,
        },
      };
    } catch (e) {
      console.error(`An error occurred while mapping this token parsed :`, tokenParsed);

      // FIXME remove the 2 following lines when ticket-33686 will be treated
      console.error(e, logoutUrl);
      window.open(logoutUrl, '_self');

      throw e;
    }
  }

  return {
    authenticated: keycloak.authenticated,
    token: keycloak.token,
    tokenParsed: keycloak.tokenParsed,
    user,
    isTokenRefreshed: false,
    logoutUrl,
  };
}

let isUpdatingToken = false;

async function updateToken(keycloak: Keycloak) {
  const tokenLifespan = 60;

  if (!isUpdatingToken) {
    isUpdatingToken = true;

    await keycloak.updateToken(tokenLifespan).finally(() => {
      isUpdatingToken = false;
    });
  }
}

function getLogoutUrl(soaConfig: SoaConfig, idToken: string | undefined): string {
  const {
    espaceDigitalHomeUrl,
    authentication: { realm, url: authenticationUrl },
  } = soaConfig;

  const logoutEspaceProQueryParams = `auth_url_return=${encodeURIComponent(espaceDigitalHomeUrl)}`;
  const logoutEspaceProUrl = `${espaceDigitalHomeUrl}/index.php/auth/log/out?${logoutEspaceProQueryParams}`;

  const logoutUrlQueryParams = `id_token_hint=${idToken?.toString() ?? ''}&post_logout_redirect_uri=${encodeURIComponent(
    import.meta.env.DEV ? 'http://localhost:8080' : logoutEspaceProUrl,
  )}`;

  return `${authenticationUrl}/realms/${realm}/protocol/openid-connect/logout?${logoutUrlQueryParams}`;
}

export function logout(state: AuthenticationState) {
  window.open(state.logoutUrl, '_self');
}

function createAuthentication(soaConfig: SoaConfig, keycloak: Keycloak): Authentication {
  const getState = (): AuthenticationState => mapStateFromKeycloak(soaConfig, keycloak);

  return {
    getState,
    authenticate: async (): Promise<AuthenticationState> => {
      if (!keycloak.authenticated) {
        await keycloak.login();
      }

      return getState();
    },
    updateToken: () => updateToken(keycloak),
  };
}

function getDebugListener(debug: boolean): Listener {
  return (state, eventType, error) => {
    if (error) {
      console.error(`error during ${eventType}`, error, state);
      captureError(error);
    } else if (debug) {

      console.log(`authentication: ${eventType}`, state);
    }
  };
}

export function listenFor(eventTypes: EventType[], listener: Listener) {
  return async (state: AuthenticationState, currentEventType: EventType, ...args: unknown[]): Promise<void> => {
    if (eventTypes.includes(currentEventType)) {
      return Promise.resolve(listener(state, currentEventType, ...args));
    }
  };
}

function getLogoutOnRefreshErrorListener() {
  return listenFor(['refreshError'], logout);
}

export async function initAuthentication(
  { url, realm, clientId, debug = false }: Config,
  authListeners: Listener[] = [],
): Promise<void> {
  authenticationPromise = (async () => {
    const soaConfig = await getConfig();

    const keycloak = await createKeycloak({ url, realm, clientId });

    const allAuthListeners: Listener[] = [getDebugListener(debug), getLogoutOnRefreshErrorListener(), ...authListeners];

    const callListeners =
      (eventType: EventType) =>
        (...args: unknown[]) =>
          Promise.all(
            allAuthListeners.map(listener => listener(mapStateFromKeycloak(soaConfig, keycloak), eventType, ...args)),
          );

    Object.assign(keycloak, {
      onAuthSuccess: callListeners('success'),
      onAuthError: callListeners('error'),
      onAuthRefreshSuccess: callListeners('refreshSuccess'),
      onAuthRefreshError: callListeners('refreshError'),
      onAuthLogout: callListeners('logout'),
      onTokenExpired: callListeners('tokenExpired'),
    });

    await keycloak.init({ responseMode: 'query', checkLoginIframe: false, enableLogging: debug });

    return createAuthentication(soaConfig, keycloak);
  })();

  await authenticationPromise;
}

export async function getAuthentication(): Promise<Authentication> {
  const authentication = await authenticationPromise;

  if (!authentication) {
    throw new AuthenticationError("Aucune instance d'authentification trouvée.");
  }

  return authentication;
}

export async function getLoggedInUser(): Promise<LoggedInUser> {
  const user = (await getAuthentication()).getState().user;

  if (user === undefined) {
    throw new Error('Utilisateur authentifié introuvable.');
  }

  return user;
}

export async function isAuthenticationFresh(): Promise<boolean> {
  const { getState } = await getAuthentication();

  const { authenticated, tokenParsed } = getState();

  if (authenticated) {
    const FRESH_AUTHENTICATION_DELAY = 3;

    if (tokenParsed === undefined) {
      throw new AuthenticationError('Token parsed non trouvé.');
    }

    if (tokenParsed.iat === undefined) {
      throw new AuthenticationError('iat non trouvé dans token parsed.');
    }

    return (Date.now() / 1000) - tokenParsed.iat <= FRESH_AUTHENTICATION_DELAY;
  }

  return false;
}

export async function setTokenAsRefreshed(): Promise<void> {
  (await getAuthentication()).getState().isTokenRefreshed = true;
}

export async function isTokenRefreshed(): Promise<boolean> {
  return (await getAuthentication()).getState().isTokenRefreshed;
}
